import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { confirmOrder } from '../api/checkout';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';

function formatPrice(cents) {
  return '$' + (cents / 100).toFixed(2);
}

export default function OrderConfirmation() {
  const [searchParams] = useSearchParams();
  const { fetchCart } = useCart();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setError('No session found.');
      setLoading(false);
      return;
    }
    confirmOrder(sessionId)
      .then(res => {
        setOrder(res.data);
        fetchCart();
      })
      .catch(() => setError('Could not load order details.'))
      .finally(() => setLoading(false));
  }, [searchParams, fetchCart]);

  if (loading) return <div className="container section" style={{ textAlign: 'center' }}>Loading...</div>;

  if (error) return (
    <div className="container section" style={{ textAlign: 'center' }}>
      <h2>Oops</h2>
      <p style={{ color: 'var(--warm-gray)', margin: '16px 0' }}>{error}</p>
      <Link to="/shop" className="btn btn-primary">Continue Shopping</Link>
    </div>
  );

  return (
    <div className="container section" style={{ maxWidth: '600px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <div style={{ fontSize: '3rem', marginBottom: '16px', color: 'var(--forest)' }}>&#10003;</div>
        <h1>Order Confirmed</h1>
        <p style={{ color: 'var(--warm-gray)', marginTop: '8px' }}>Thank you for your purchase!</p>
      </div>

      {order && (
        <div className="cart-summary">
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <span className="mono" style={{ color: 'var(--warm-gray)' }}>Order Number</span>
              <p style={{ fontWeight: '700', fontSize: '1.1rem' }}>{order.order_number}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span className="mono" style={{ color: 'var(--warm-gray)' }}>Date</span>
              <p>{new Date(order.created_at).toLocaleDateString()}</p>
            </div>
          </div>

          {order.items?.map((item, i) => (
            <div className="cart-item" key={i}>
              <div className="cart-item-details">
                <div className="cart-item-name">{item.product_name}</div>
                {(item.variant || item.color) && <div className="cart-item-variant">{item.variant || item.color}</div>}
                <div className="cart-item-variant">Qty: {item.quantity}</div>
              </div>
              <div className="cart-item-price">{formatPrice(item.price_cents * item.quantity)}</div>
            </div>
          ))}

          <div className="cart-totals">
            <div className="cart-total-row"><span>Subtotal</span><span>{formatPrice(order.subtotal_cents)}</span></div>
            <div className="cart-total-row"><span>Shipping</span><span>{order.shipping_cents === 0 ? 'FREE' : formatPrice(order.shipping_cents)}</span></div>
            <div className="cart-total-row grand"><span>Total</span><span>{formatPrice(order.total_cents)}</span></div>
          </div>
        </div>
      )}

      {/* Guest account creation prompt */}
      {!user && order && (
        <div className="cart-summary" style={{ marginTop: '24px', textAlign: 'center' }}>
          <h3 style={{ marginBottom: '8px' }}>Want to track your order?</h3>
          <p style={{ color: 'var(--warm-gray)', fontSize: '0.9rem', marginBottom: '16px' }}>
            Create an account with <strong>{order.email}</strong> to view your order history and get updates.
          </p>
          <Link to="/register" className="btn btn-primary">Create Account</Link>
          <p style={{ marginTop: '12px', fontSize: '0.8rem', color: 'var(--warm-gray)' }}>
            or <Link to="/order-lookup" style={{ color: 'var(--forest)' }}>look up your order</Link> anytime with your email
          </p>
        </div>
      )}

      <div style={{ textAlign: 'center', marginTop: '24px' }}>
        <Link to="/shop" className="btn btn-secondary">Continue Shopping</Link>
      </div>
    </div>
  );
}
